import React from 'react';
import moment from 'moment';
import cn from '@/utils/cn';
import CurrencyFormat from 'react-currency-format';
import { DownloadPdfIcon, ErrorIcon } from '@/components/Icons';
import { CheckboxCell, CheckboxCellClass } from '@/components/DataTable/CheckboxCell';
import { TMonthlyStatementRow } from '../../hooks/useMonthlyStatements';

const CellClass = cn(['pl-4', 'pr-6', 'py-4']);

interface MonthlyStatementTableRowProps {
  index: number;
  row: TMonthlyStatementRow;
  isPdfLoading: boolean;
  onDownload: (year: number, month: number) => void;
  isSelected: boolean;
  onToggleSelect: () => void;
}

const MonthlyStatementTableRow: React.FC<MonthlyStatementTableRowProps> = ({
  index,
  row,
  isPdfLoading,
  onDownload,
  isSelected,
  onToggleSelect
}) => {
  const label = moment(`${row.year}-${row.month}`, 'YYYY-M').format('MMMM YYYY');

  return (
    <tr
      className={cn(
        'border-b hover:cursor-pointer transition-colors',
        isSelected ? 'bg-emerge-teal-5' : 'bg-white hover:bg-emerge-base-2'
      )}
      key={`statement-${index}`}
      onClick={onToggleSelect}>
      <td className={CheckboxCellClass}>
        <CheckboxCell
          checked={isSelected}
          onChange={onToggleSelect}
          ariaLabel={`Select statement for ${label}`}
        />
      </td>
      <td className={cn(CellClass, 'text-sm text-black')}>
        {moment(`${row.year}-${row.month}`, 'YYYY-M').format('MMMM YYYY')}
      </td>
      <td className={cn(CellClass, 'text-sm text-emerge-navy')}>
        <CurrencyFormat
          value={Number(row.openingBalance).toFixed(2)}
          displayType="text"
          thousandSeparator
          prefix="$"
          decimalScale={2}
        />
      </td>
      <td className={cn(CellClass, 'text-sm text-emerge-navy')}>
        <CurrencyFormat
          value={Number(row.closingBalance).toFixed(2)}
          displayType="text"
          thousandSeparator
          prefix="$"
          decimalScale={2}
        />
      </td>
      <td className={cn(CellClass, 'text-sm text-emerge-navy')}>
        {isPdfLoading ? (
          <span className="text-xs text-black/20">Loading...</span>
        ) : row.pdfError ? (
          row.pdfError.isRetryable ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(row.year, row.month);
              }}
              className="flex items-center gap-1 text-xs font-medium text-error-darkred hover:text-error-darkred/80"
              title={`${row.pdfError.message} (${row.pdfError.status})`}
              aria-label={`Retry download for ${label} (previous error: ${row.pdfError.message})`}>
              <ErrorIcon size="xs" />
              Retry
            </button>
          ) : (
            <div className="flex items-center gap-1 text-xs font-medium text-error-darkred bg-error-lightred px-2 py-1 rounded">
              <ErrorIcon size="xs" />
              <span title={`${row.pdfError.message} (${row.pdfError.status})`}>Not available</span>
            </div>
          )
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(row.year, row.month);
            }}
            className="flex items-center gap-1 text-xs font-medium text-emerge-navy hover:text-emerge-navy-2"
            aria-label={`Download PDF for ${label}`}>
            <DownloadPdfIcon size="xs" />
            Download pdf
          </button>
        )}
      </td>
    </tr>
  );
};

export default MonthlyStatementTableRow;
